/**
 * SplitFlapDisplay.jsx
 *
 * Main split-flap/Solari board component for the Operations Center.
 * Displays flights in a retro airport departure board style.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SplitFlapRow, COLUMNS } from './SplitFlapRow.jsx';
import './splitflap.css';

/**
 * Custom hook to track flight changes for animations
 */
function useSplitFlapState(flights, maxRows = 16) {
  const [displayedFlights, setDisplayedFlights] = useState([]);
  const [enteringIds, setEnteringIds] = useState(new Set());
  const [exitingIds, setExitingIds] = useState(new Set());
  const prevFlightIdsRef = useRef(new Set());

  useEffect(() => {
    const currentIds = new Set(flights.slice(0, maxRows).map(f => f.id));
    const prevIds = prevFlightIdsRef.current;

    // Find new flights (entering)
    const newIds = new Set();
    currentIds.forEach(id => {
      if (!prevIds.has(id)) {
        newIds.add(id);
      }
    });

    // Find removed flights (exiting)
    const removedIds = new Set();
    prevIds.forEach(id => {
      if (!currentIds.has(id)) {
        removedIds.add(id);
      }
    });

    // Trigger entering animations
    if (newIds.size > 0) {
      setEnteringIds(newIds);
      setTimeout(() => setEnteringIds(new Set()), 450);
    }

    // Update displayed flights
    setDisplayedFlights(flights.slice(0, maxRows));

    // Update prev ref for next comparison
    prevFlightIdsRef.current = currentIds;
  }, [flights, maxRows]);

  return { displayedFlights, enteringIds, exitingIds };
}

/**
 * Sort flights for display
 * Order: scheduled (by dep time) -> boarding/ground ops -> in flight -> completed
 */
function sortFlightsForDisplay(flights) {
  const phaseOrder = {
    scheduled: 0,
    boarding: 1,
    pushback: 2,
    taxi_out: 3,
    takeoff: 4,
    climb: 5,
    cruise: 6,
    descent: 7,
    approach: 8,
    landing: 9,
    taxi_in: 10,
    arrived: 11,
    completed: 12,
    cancelled: 13,
  };

  return [...flights].sort((a, b) => {
    // First by phase order
    const phaseA = phaseOrder[a.phase] ?? 99;
    const phaseB = phaseOrder[b.phase] ?? 99;

    if (phaseA !== phaseB) return phaseA - phaseB;

    // Then by scheduled departure time
    const timeA = a.scheduledDeparture ? new Date(a.scheduledDeparture).getTime() : 0;
    const timeB = b.scheduledDeparture ? new Date(b.scheduledDeparture).getTime() : 0;

    return timeA - timeB;
  });
}

/**
 * SplitFlapDisplay Component
 *
 * @param {Array} flights - Array of flight objects to display
 * @param {Object} airportWeather - Weather data by ICAO code
 * @param {number} maxRows - Maximum number of rows to display
 * @param {string} title - Optional title above the board
 * @param {boolean} isArrivals - If true, format as arrivals board
 */
export function SplitFlapDisplay({
  flights = [],
  airportWeather = {},
  maxRows = 16,
  title = 'OPERATIONS BOARD',
  isArrivals = false,
}) {
  const { t } = useTranslation();
  // Filter and sort flights for display
  const sortedFlights = useMemo(() => {
    // Exclude completed/cancelled flights older than 5 minutes
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const activeFlights = flights.filter(f => {
      if (f.status === 'completed' || f.status === 'cancelled') {
        const completedAt = f.completedAt ? new Date(f.completedAt).getTime() : 0;
        return completedAt > fiveMinutesAgo;
      }
      return true;
    });

    return sortFlightsForDisplay(activeFlights);
  }, [flights]);

  // Track state for animations
  const { displayedFlights, enteringIds, exitingIds } = useSplitFlapState(sortedFlights, maxRows);

  return (
    <div className="splitflap-display">
      {/* Title */}
      {title && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '700',
            color: '#ffb300',
            textShadow: '0 0 10px rgba(255, 179, 0, 0.4)',
            letterSpacing: '3px',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #252525',
          }}
        >
          {title}
        </div>
      )}

      {/* Column Headers */}
      <div className="splitflap-header">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            className={`splitflap-header-cell ${col.className}`}
          >
            {t(`ops.${col.labelKey}`)}
          </div>
        ))}
      </div>

      {/* Flight Rows */}
      <div
        className="splitflap-body"
        style={{ maxHeight: `${maxRows * 36}px`, overflowY: 'auto' }}
      >
        {displayedFlights.length === 0 ? (
          <div className="splitflap-empty">
            <div className="splitflap-empty-icon">--</div>
            <div>{t('ops.noScheduledFlights')}</div>
          </div>
        ) : (
          displayedFlights.map((flight) => (
            <SplitFlapRow
              key={flight.id}
              flight={flight}
              airportWeather={airportWeather}
              isEntering={enteringIds.has(flight.id)}
              isExiting={exitingIds.has(flight.id)}
              animate={true}
              isArrivals={isArrivals}
            />
          ))
        )}
      </div>

      {/* Footer stats */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '8px',
          paddingTop: '8px',
          borderTop: '1px solid #1a1a1a',
          fontSize: '10px',
          color: '#525252',
        }}
      >
        <span>
          {displayedFlights.filter(f => f.phase === 'scheduled').length} {t('ops.scheduled')}
        </span>
        <span>
          {displayedFlights.filter(f =>
            ['takeoff', 'climb', 'cruise', 'descent', 'approach', 'landing'].includes(f.phase)
          ).length} {t('ops.inFlight')}
        </span>
        <span>
          {displayedFlights.filter(f => f.weatherHold).length} {t('ops.wxHoldCount')}
        </span>
        <span>
          {displayedFlights.filter(f => (f.totalDelayMinutes || 0) >= 30).length} {t('ops.delayed')}
        </span>
      </div>
    </div>
  );
}

export default SplitFlapDisplay;
